import {useEffect, useState} from 'react';
import {useIntl, IntlShape} from 'react-intl';
import axios from 'axios';
import {Card, Form, OverlayTrigger, Tooltip} from 'react-bootstrap';
import Button from '../../../../../Components/Button';
import FilterSelect, {FilterOption} from '../../../../../app/common/components/select/FilterSelect';
import {ApiIpZone, ApiNumberIpRestrictionUpdate} from '../../../Api/telephonie';
import {
  useGetIpRestriction,
  useGetLimitation,
  useGetNumberIpRestriction,
  useGetTrunkNumeros,
  useUpdateIpRestriction,
  useUpdateNumberIpRestriction,
} from '../../../Hooks/TelephonieManager';
import Loading from '../../../../../app/common/components/Loading';

/**
 * « Filtrage IP » (Voice/FilteredIP.aspx) : restriction géographique par zone autorisée +
 * exceptions IP libres, au niveau service (« Tous les numéros ») ou d'un numéro, qui peut
 * soit suivre la restriction du service, soit la remplacer par la sienne.
 */

const TOUS_LES_NUMEROS = '*';

const ZONES: ApiIpZone[] = ['nowhere', 'switzerland', 'france', 'europe', 'world'];

export const ZONE_LABEL_ID: Record<ApiIpZone, string> = {
  nowhere: 'TELEPHONIE.RESTRICTION.IP.ZONE.NULLE_PART',
  switzerland: 'TELEPHONIE.RESTRICTION.IP.ZONE.SUISSE',
  france: 'TELEPHONIE.RESTRICTION.IP.ZONE.FRANCE',
  europe: 'TELEPHONIE.RESTRICTION.IP.ZONE.EUROPE',
  world: 'TELEPHONIE.RESTRICTION.IP.ZONE.MONDE',
};

const PAYS_EUROPE = [
  'Allemagne',
  'Andorre',
  'Autriche',
  'Belgique',
  'Bulgarie',
  'Chypre',
  'Croatie',
  'Danemark',
  'Espagne',
  'Estonie',
  'Finlande',
  'France',
  'Grande-Bretagne',
  'Grèce',
  'Hongrie',
  'Irlande',
  'Italie',
  'Lettonie',
  'Liechtenstein',
  'Lituanie',
  'Luxembourg',
  'Malte',
  'Monaco',
  'Pays-Bas',
  'Pologne',
  'Portugal',
  'République tchèque',
  'Roumanie',
  'Slovaquie',
  'Slovénie',
  'Suède',
  'Suisse',
];

/** L'API relaie les erreurs de la plateforme voix en RFC 9457 (`detail`, `status`). */
interface ApiProblem {
  detail?: string;
}

const messageErreurRestriction = (error: unknown, intl: IntlShape): string => {
  if (axios.isAxiosError(error) && error.response) {
    if (error.response.status === 409) {
      return intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.ERROR_LOCKED'});
    }
    const detail = (error.response.data as ApiProblem | undefined)?.detail;
    if (error.response.status === 422 && detail) {
      return detail;
    }
  }
  return intl.formatMessage({id: 'COMMON.INTERNAL_ERROR'});
};

const ipExceptionsDepuisTexte = (texte: string): string[] =>
  texte
    .split(/[,;]/)
    .map((valeur) => valeur.trim())
    .filter((valeur) => valeur !== '');

const useNumeroOptions = (
  serviceId: number
): {numeroOptions: FilterOption<string>[]; isLoading: boolean} => {
  const intl = useIntl();
  const {data, isLoading} = useGetTrunkNumeros(serviceId);
  const numeroOptions: FilterOption<string>[] = [
    {
      value: TOUS_LES_NUMEROS,
      label: intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.TOUS_LES_NUMEROS'}),
    },
    ...(data?.numeros ?? []).map((numero) => ({
      value: String(numero.numeroId),
      label: numero.numero,
    })),
  ];
  return {numeroOptions, isLoading};
};

const ZoneRadios = ({
  zone,
  setZone,
  disabled,
  aucuneSelection = false,
}: {
  zone: ApiIpZone;
  setZone: (zone: ApiIpZone) => void;
  /** Verrou produit : les zones ne sont plus modifiables. */
  disabled?: boolean;
  /** Aucune zone cochée (le numéro suit le service), mais toutes restent cliquables. */
  aucuneSelection?: boolean;
}): JSX.Element => {
  const intl = useIntl();
  return (
    <div className="d-flex flex-column gap-2">
      {ZONES.map((option) => (
        <div key={option} className="d-flex align-items-center gap-2">
          <Form.Check
            type="radio"
            id={`zone-ip-${option}`}
            name="zone-ip"
            label={intl.formatMessage({id: ZONE_LABEL_ID[option]})}
            className="fs-6"
            disabled={disabled}
            checked={!aucuneSelection && zone === option}
            onChange={() => setZone(option)}
          />
          {option === 'europe' && (
            <OverlayTrigger
              placement="right"
              overlay={
                <Tooltip>
                  {PAYS_EUROPE.map((pays) => (
                    <div key={pays}>{pays}</div>
                  ))}
                </Tooltip>
              }
            >
              <i className="bi bi-question-circle-fill text-primary cursor-pointer" />
            </OverlayTrigger>
          )}
        </div>
      ))}
    </div>
  );
};

interface EnteteEtSelecteurProps {
  numeroOptions: FilterOption<string>[];
  numero: string;
  setNumero: (numero: string) => void;
}

const EnteteEtSelecteur = ({
  numeroOptions,
  numero,
  setNumero,
}: EnteteEtSelecteurProps): JSX.Element => {
  const intl = useIntl();
  return (
    <>
      <div className="d-flex align-items-center gap-2 mb-3">
        <span className="fs-3">{intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.IP_TITLE'})}</span>
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip>{intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.IP_DESCRIPTION'})}</Tooltip>
          }
        >
          <i className="bi bi-question-circle-fill text-primary fs-4 cursor-pointer" />
        </OverlayTrigger>
      </div>
      <p className="fs-6 mb-5">
        {intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.IP_DESCRIPTION'})}
      </p>
      <div className="fs-6 mb-2">
        {intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.IP_AUTORISER_DEPUIS'})}
      </div>
      <div style={{minWidth: 260, maxWidth: 260}} className="mb-5">
        <FilterSelect options={numeroOptions} value={numero} onChange={setNumero} />
      </div>
    </>
  );
};

const ExceptionsEtConfirmer = ({
  exceptionEditee,
  setExceptionEditee,
  teamsAllowed,
  erreur,
  disabled,
  onConfirm,
}: {
  exceptionEditee: string;
  setExceptionEditee: (valeur: string) => void;
  teamsAllowed: boolean;
  erreur: string | null;
  disabled: boolean;
  onConfirm: () => void;
}): JSX.Element => {
  const intl = useIntl();
  return (
    <>
      {teamsAllowed && (
        <Form.Check
          className="mb-4"
          type="checkbox"
          id="teams-info"
          checked
          disabled
          label={intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.IP_TEAMS'})}
        />
      )}
      <div className="fs-6 mb-2 d-flex align-items-center gap-2">
        {intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.IP_EXCEPTIONS_LABEL'})}
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip>
              <div>
                {intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.IP_EXCEPTIONS_BULLE_INTRO'})}
              </div>
              <div className="mt-2">
                {intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.IP_EXCEPTIONS_BULLE_EXEMPLE'})}
              </div>
              <div>213.147.25.16;214.65.14.12</div>
              <div>{intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.IP_EXCEPTIONS_BULLE_OU'})}</div>
              <div>214.65.14.1-214.65.14.10</div>
              <div>{intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.IP_EXCEPTIONS_BULLE_OU'})}</div>
              <div>213.147.25.16</div>
            </Tooltip>
          }
        >
          <i className="bi bi-question-circle-fill text-primary cursor-pointer" />
        </OverlayTrigger>
      </div>
      <Form.Control
        style={{maxWidth: 480}}
        className="mb-5"
        value={exceptionEditee}
        onChange={(e) => setExceptionEditee(e.target.value)}
      />
      {erreur && <div className="fw-bold text-danger mb-3">{erreur}</div>}
      <div className="text-center">
        <Button
          label={intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.CONFIRMER'})}
          disabled={disabled}
          onClick={onConfirm}
        />
      </div>
    </>
  );
};

interface ServiceViewProps extends EnteteEtSelecteurProps {
  serviceId: number;
  onConfirm: () => void;
}

const IpRestrictionServiceView = ({
  serviceId,
  numeroOptions,
  numero,
  setNumero,
  onConfirm,
}: ServiceViewProps): JSX.Element | null => {
  const intl = useIntl();
  const {
    data: limitation,
    isLoading: limitationLoading,
    isError: limitationError,
  } = useGetLimitation(serviceId);
  const {data, isLoading, isError} = useGetIpRestriction(serviceId);
  const updateIpRestriction = useUpdateIpRestriction(serviceId);
  const [zone, setZone] = useState<ApiIpZone>('switzerland');
  const [ipExceptionsTexte, setIpExceptionsTexte] = useState<string>('');
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    if (data) {
      setZone(data.zone);
      setIpExceptionsTexte(data.ipExceptions.join(', '));
    }
  }, [data]);

  if (isError || limitationError) {
    return <p>{intl.formatMessage({id: 'COMMON.INTERNAL_ERROR'})}</p>;
  }

  if (isLoading || limitationLoading || !data || !limitation) {
    return <Loading className="col-md-6" />;
  }

  const confirmer = (): void => {
    setErreur(null);
    updateIpRestriction.mutate(
      {...data, zone, ipExceptions: ipExceptionsDepuisTexte(ipExceptionsTexte)},
      {
        onSuccess: onConfirm,
        onError: (error) => setErreur(messageErreurRestriction(error, intl)),
      }
    );
  };

  return (
    <>
      <EnteteEtSelecteur numeroOptions={numeroOptions} numero={numero} setNumero={setNumero} />
      {limitation.lockedByProductLine && (
        <p className="text-muted fs-7 mb-3">
          {intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.LOCKED_HELP'})}
        </p>
      )}
      <div className="mb-5">
        <ZoneRadios zone={zone} setZone={setZone} />
      </div>
      <ExceptionsEtConfirmer
        exceptionEditee={ipExceptionsTexte}
        setExceptionEditee={setIpExceptionsTexte}
        teamsAllowed={data.msTeamsAllowed}
        erreur={erreur}
        disabled={limitation.lockedByProductLine || updateIpRestriction.isLoading}
        onConfirm={confirmer}
      />
    </>
  );
};

interface NumberViewProps extends EnteteEtSelecteurProps {
  serviceId: number;
  numberId: number;
  onConfirm: () => void;
}

const NumberIpRestrictionView = ({
  serviceId,
  numberId,
  numeroOptions,
  numero,
  setNumero,
  onConfirm,
}: NumberViewProps): JSX.Element | null => {
  const intl = useIntl();
  const {
    data: limitation,
    isLoading: limitationLoading,
    isError: limitationError,
  } = useGetLimitation(serviceId);
  const {data, isLoading, isError} = useGetNumberIpRestriction(serviceId, numberId);
  const updateNumberIpRestriction = useUpdateNumberIpRestriction(serviceId, numberId);
  const [suitService, setSuitService] = useState<boolean>(true);
  const [zone, setZone] = useState<ApiIpZone>('switzerland');
  const [ipExceptionsTexte, setIpExceptionsTexte] = useState<string>('');
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    if (data) {
      setSuitService(data.inherited);
      setZone(data.zone);
      setIpExceptionsTexte(data.ipExceptions.join(', '));
    }
  }, [data]);

  if (isError || limitationError) {
    return <p>{intl.formatMessage({id: 'COMMON.INTERNAL_ERROR'})}</p>;
  }

  if (isLoading || limitationLoading || !data || !limitation) {
    return <Loading className="col-md-6" />;
  }

  const confirmer = (): void => {
    setErreur(null);
    const update: ApiNumberIpRestrictionUpdate = suitService
      ? {inherited: true}
      : {zone, ipExceptions: ipExceptionsDepuisTexte(ipExceptionsTexte)};
    updateNumberIpRestriction.mutate(update, {
      onSuccess: onConfirm,
      onError: (error) => setErreur(messageErreurRestriction(error, intl)),
    });
  };

  return (
    <>
      <EnteteEtSelecteur numeroOptions={numeroOptions} numero={numero} setNumero={setNumero} />
      {limitation.lockedByProductLine && (
        <p className="text-muted fs-7 mb-3">
          {intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.LOCKED_HELP'})}
        </p>
      )}
      <div className="mb-5">
        <Form.Check
          type="radio"
          id="zone-ip-suit-service"
          name="zone-ip"
          label={intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.IP_SUIVRE_SERVICE'})}
          className="fs-6 mb-2"
          checked={suitService}
          onChange={() => setSuitService(true)}
        />
        <ZoneRadios
          zone={zone}
          disabled={limitation.lockedByProductLine}
          aucuneSelection={suitService}
          setZone={(nouvelleZone) => {
            setSuitService(false);
            setZone(nouvelleZone);
          }}
        />
      </div>
      <ExceptionsEtConfirmer
        exceptionEditee={ipExceptionsTexte}
        setExceptionEditee={setIpExceptionsTexte}
        teamsAllowed={data.msTeamsAllowed}
        erreur={erreur}
        disabled={limitation.lockedByProductLine || updateNumberIpRestriction.isLoading}
        onConfirm={confirmer}
      />
    </>
  );
};

const RestrictionFiltrageIp = ({
  serviceId,
  onConfirm,
}: {
  serviceId: number;
  onConfirm: () => void;
}): JSX.Element => {
  const [numero, setNumero] = useState<string>(TOUS_LES_NUMEROS);
  const {numeroOptions} = useNumeroOptions(serviceId);

  return (
    <div className="mt-4">
      <Card>
        <Card.Body>
          {numero === TOUS_LES_NUMEROS ? (
            <IpRestrictionServiceView
              serviceId={serviceId}
              numeroOptions={numeroOptions}
              numero={numero}
              setNumero={setNumero}
              onConfirm={onConfirm}
            />
          ) : (
            <NumberIpRestrictionView
              key={numero}
              serviceId={serviceId}
              numberId={Number(numero)}
              numeroOptions={numeroOptions}
              numero={numero}
              setNumero={setNumero}
              onConfirm={onConfirm}
            />
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default RestrictionFiltrageIp;
